import {useRef, useState, useEffect, useCallback, memo} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import _isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import _capitalize from 'lodash/capitalize';
import _isEqual from 'lodash/isEqual';
import _isFunction from 'lodash/isFunction';
import clsx from 'clsx';
import makeStyles from '@mui/styles/makeStyles';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Stack from '@mui/material/Stack';
import {Typography} from '@mui/material';

const useMenuStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    display: 'flex',
    width: '100%',
    flexBasis: '100%',
  },
  paperRoot: {
    backgroundColor: theme.palette.background.paperDarker,
  },
  popper: {
    zIndex: 999,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
  },
  button: {
    color: theme.palette.mode === "light" ? "#000000" : "#FFFFFF",
    fontWeight: 400,
    paddingLeft: 16,
    paddingRight: 16,
    borderRadius: 10,
    textTransform: 'none',
    backgroundColor: theme.palette.background.paper,

    "&.open":{
      backgroundColor: theme.palette.background.paper,
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    },
    "&.MuiButton-root": {
      display: 'flex',
      flexBasis: '100%',
    },
    "&.agyleColor": {
      backgroundColor: theme.palette.primary.main,
    }
  },
  menu: {
    flexGrow: 1,
    paddingTop: 0,
    display: 'flex',
    paddingBottom: 0,
    flexDirection: 'column',
  },
  optionLabel: {
    flexGrow: 1,
    color: theme.palette.mode === 'light' ? '#000' : '#FFF',

    "&:hover, &.Mui-selected, &.Mui-selected:hover": {
      backgroundColor: theme.palette.primary.main,
      color: "#FFF",
    },
    "&.Mui-selected > p": {
      fontWeight: 700,
    },
    "&:last-child": {
      borderBottomLeftRadius: 6,
      borderBottomRightRadius: 6,
    }
  }
}));

const BUTTON_HEIGHT = 36;
const MAX_VISIBLE_ITEMS = 3;

const AgyleMenuList = ({
                         options,
                         value,
                         valuePath,
                         descriptionPath,
                         onSelectItem,
                         buttonLabel,
                         dropdownPlacement,
                         disabled,
                         maxVisibleItems,
                         renderOptionContent,
                         menuItemStyle,
                         isOptionDisabled,
                         matchWidth,
                         agyleColor,
}) => {
  const classes = useMenuStyles();
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const menuListRef = useRef(null);

  const handleToggle = (event) => {
    event.stopPropagation();

    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    event.stopPropagation();

    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);
  };

  function handleListKeyDown(event) {
    event.stopPropagation();

    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  }

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const handleOnListItemClick = (event, value, a, b,c) => {
    event.stopPropagation();

    if (onSelectItem) {
      onSelectItem(event, value);
      handleClose(event);
    } else {
      handleClose(event);
    }
  }

  const calculateMaxVisibleHeight = useCallback(() => {
    let height = BUTTON_HEIGHT;

    if (maxVisibleItems) {
      height *= maxVisibleItems;
    } else {
      height *= MAX_VISIBLE_ITEMS;
    }

    return height;
  }, [maxVisibleItems]);

  const getOptionDescription = useCallback((option) => {
    return _get(option, descriptionPath) || "Unk";
  }, [descriptionPath]);

  const isValueEqual = useCallback((itemValue, currValue) => {
    const curr = _get(currValue, valuePath);
    const itemVal = _get(itemValue, valuePath);

    return _isEqual(curr, itemVal);
  }, [valuePath]);

  return (
    <Stack direction="row" spacing={2} className={classes.root}>
      <Button
        ref={anchorRef}
        id="composition-button"
        onClick={handleToggle}
        style={{
          height: BUTTON_HEIGHT,
          flexBasis: matchWidth ? menuListRef?.current?.getBoundingClientRect().innerWidth : '',
      }}
        aria-expanded={open ? 'true' : undefined}
        aria-controls={open ? 'composition-menu' : undefined}
        className={clsx(classes.button, open && 'open', agyleColor && 'agyleColor')}
      >
        {buttonLabel}
      </Button>

      <Popper
        transition
        open={open}
        disablePortal
        role={undefined}
        className={classes.popper}
        anchorEl={anchorRef.current}
        placement={dropdownPlacement}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom-start' ? 'left top' : 'left bottom',
            }}
          >
            <Paper
              className={classes.popper}
              style={{ width: anchorRef?.current && open && anchorRef?.current?.getBoundingClientRect()?.width}}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  ref={menuListRef}
                  disabled={disabled}
                  autoFocusItem={open}
                  id="composition-menu"
                  onKeyDown={handleListKeyDown}
                  aria-labelledby="composition-button"
                  style={{ maxHeight: calculateMaxVisibleHeight() }}
                  className={clsx(classes.menu, 'scroll-normal-white')}
                >
                  {!_isEmpty(options) && options.map((option, opIdx) => {
                    const isSelected = isValueEqual(value, option);
                    const optionDisabled = _isFunction(isOptionDisabled) ? !!isOptionDisabled(option, opIdx) : false;

                    return (
                      <MenuItem
                        key={opIdx}
                        selected={isSelected}
                        disabled={optionDisabled}
                        style={{ height: BUTTON_HEIGHT }}
                        className={clsx(classes.optionLabel, menuItemStyle)}
                        onClick={(event) => handleOnListItemClick(event, option)}
                        // disabled={() => _isFunction(isOptionDisabled) ? !!isOptionDisabled(option, opIdx) : false}
                      >
                        {renderOptionContent ? renderOptionContent(option, opIdx) :
                          <Typography variant="subtitle1" fontWeight={isSelected ? 500 : 400}>
                            {t(
                              _capitalize(getOptionDescription(option))
                            )}
                          </Typography>
                        }
                      </MenuItem>
                    );
                  })}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
  </Stack>
  );
}

AgyleMenuList.propTypes = {
  style: PropTypes.object,
  name: PropTypes.string,
  disabled: PropTypes.bool,
  endIcon: PropTypes.object,
  agyleColor: PropTypes.bool,
  matchWidth: PropTypes.bool,
  startIcon: PropTypes.object,
  valuePath: PropTypes.string,
  onSelectItem: PropTypes.func,
  menuItemStyle: PropTypes.string,
  isOptionDisabled: PropTypes.func,
  descriptionPath: PropTypes.string,
  disableTouchRipple: PropTypes.bool,
  maxVisibleItems: PropTypes.number,
  renderOptionContent: PropTypes.func,
  dropdownPlacement: PropTypes.string,
  buttonLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.array]),
};

AgyleMenuList.defaultProps = {
  disabled: false,
  valuePath: '__id',
  descriptionPath: '__description',
  dropdownPlacement: 'bottom-start',
  maxVisibleItems: MAX_VISIBLE_ITEMS,
}

export default memo(AgyleMenuList, _isEqual);


